import Config from '@config';
import {
  MeteringSeal,
  MeteringSealQueryParams,
  SealViewModel,
} from '@gasbi/shared/common/features/meteringSeal/types';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useSelector } from 'react-redux';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { Pagination } from '@/features/types';
import { cachedItemByIdSelector, infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

export const meteringSealCommercialApi = createApi({
  reducerPath: 'meteringSealCommercialApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/commercial-consumers/seals`,
  }),
  tagTypes: ['MeteringSealsCommercial', 'MeteringSealCommercial'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getMeteringSealCommercial: builder.query<
      SealViewModel[],
      MeteringSealQueryParams
    >({
      query: params => ({
        url: '/query',
        params,
      }),
      providesTags: () => ['MeteringSealsCommercial', 'MeteringSealCommercial'],
    }),
    getMeteringSealCommercialI: builder.infiniteQuery<
      SealViewModel[],
      MeteringSealQueryParams,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '/query',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['MeteringSealsCommercial', 'MeteringSealCommercial'],
    }),
    getMeteringSealCommercialById: builder.query<MeteringSeal, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['MeteringSealCommercial', 'MeteringSealsCommercial'],
    }),
    createMeteringSealCommercial: builder.mutation<MeteringSeal, MeteringSeal>({
      query: data => ({
        url: '/',
        method: 'POST',
        data: data,
      }),
      invalidatesTags: result => [
        'MeteringSealCommercial',
        { type: 'MeteringSealCommercial', id: result?.id },
      ],
    }),
    deleteMeteringSealCommercialById: builder.mutation<MeteringSeal, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'MeteringSealsCommercial',
        { type: 'MeteringSealCommercial', id },
      ],
    }),
  }),
});

export const {
  useGetMeteringSealCommercialQuery,
  useGetMeteringSealCommercialByIdQuery,
  useCreateMeteringSealCommercialMutation,
  useDeleteMeteringSealCommercialByIdMutation,
} = meteringSealCommercialApi;

export const useGetMeteringSealCommercialInfiniteQuery =
  normalizeInfiniteQueryData(
    meteringSealCommercialApi.useGetMeteringSealCommercialIInfiniteQuery,
  );

const getCachedMeteringSealCommercialByIdSelector = cachedItemByIdSelector(
  'meteringSealCommercialApi',
  'getMeteringSealCommercialI',
);

export const useGetCachedMeteringSealCommercialByIdSelector = id =>
  useSelector(getCachedMeteringSealCommercialByIdSelector(id));
